import { useMemo } from 'react';
import { diffDays, inRange, isFaturado, isRecebido, monthRefToRange, num, parseDate, startOfToday } from './utils';

export function useReceitasResumo({ recs, mesRef }) {
  return useMemo(() => {
    const today = startOfToday();
    const { start: mesStart, endExclusive: mesEndExclusive } = monthRefToRange(mesRef);
    const inadimplenciaCutoff = parseDate('2024-01-01');

    // Helpers (rec_dtVenc ainda não existe: fallback para rec_dtQuitAtual)
    const getRecebimentoDueDate = (item) => parseDate(item?.rec_dtVenc) || parseDate(item?.rec_dtQuitAtual);
    const isRecebimentoEmAtraso = (item) => {
      const due = getRecebimentoDueDate(item);
      if (!due) return false;
      return diffDays(due, today) < 0;
    };

    // ===========================
    // FATURAMENTO (mês): rec_dtNfPrev + rec_vlrBru
    // ===========================
    let fatPrevMesBru = 0;
    let fatRealMesBru = 0;
    let fatPrevCount = 0;
    let fatRealCount = 0;

    let fatDistFaturadoCount = 0;
    let fatDistLiberadoCount = 0;
    let fatDistBloqueadoCount = 0;

    let fatDistFaturadoBru = 0;
    let fatDistLiberadoBru = 0;
    let fatDistBloqueadoBru = 0;

    // ===========================
    // RECEBIMENTOS (mês): rec_dtQuitAtual + rec_vlrLiq
    // ===========================
    let recLiqPrevMes = 0;
    let recLiqPrevCount = 0;

    let recLiqRealMes = 0;
    let recLiqRealCount = 0;

    let recDistRecebidoLiq = 0;
    let recDistAbertoNoPrazoLiq = 0;
    let recDistAtrasoLiq = 0;

    let recDistRecebidoCount = 0;
    let recDistAbertoNoPrazoCount = 0;
    let recDistAtrasoCount = 0;

    // ===========================
    // INADIMPLÊNCIA (mês selecionado): pipeline do mês por rec_dtQuitAtual
    // base do atraso: getRecebimentoDueDate() (rec_dtVenc quando existir)
    // ===========================
    let inadimplenciaLiq = 0;
    let inadimplenciaCount = 0;
    let emAbertoNoMesLiq = 0;
    let emAbertoNoMesCount = 0;

    let semDtNfPrev = 0;
    let semDtQuit = 0;
    let semDtDue = 0;

    for (const r of recs || []) {
      const vlrBru = num(r?.rec_vlrBru);
      const vlrLiq = num(r?.rec_vlrLiq);
      const dtNfPrev = parseDate(r?.rec_dtNfPrev);
      const dtQuitPrev = parseDate(r?.rec_dtQuitAtual);
      const due = getRecebimentoDueDate(r);

      if (!dtNfPrev) semDtNfPrev += 1;
      if (!dtQuitPrev) semDtQuit += 1;
      if (!due) semDtDue += 1;

      // ===========================
      // FATURAMENTO (mês)
      // ===========================
      if (dtNfPrev && inRange(dtNfPrev, mesStart, mesEndExclusive)) {
        fatPrevCount += 1;
        fatPrevMesBru += vlrBru;

        const fat = isFaturado(r);
        const lib = Number(r?.rec_aut) === 1;

        if (fat) {
          fatRealCount += 1;
          fatRealMesBru += vlrBru;
          fatDistFaturadoCount += 1;
          fatDistFaturadoBru += vlrBru;
        } else if (lib) {
          fatDistLiberadoCount += 1;
          fatDistLiberadoBru += vlrBru;
        } else {
          fatDistBloqueadoCount += 1;
          fatDistBloqueadoBru += vlrBru;
        }
      }

      // ===========================
      // RECEBIMENTOS (mês) — visão líquida prevista/realizada por rec_dtQuitAtual
      // ===========================
      if (dtQuitPrev && inRange(dtQuitPrev, mesStart, mesEndExclusive)) {
        recLiqPrevCount += 1;
        recLiqPrevMes += vlrLiq;

        if (isRecebido(r)) {
          recLiqRealCount += 1;
          recLiqRealMes += vlrLiq;
          recDistRecebidoCount += 1;
          recDistRecebidoLiq += vlrLiq;
        } else if (due && isFaturado(r)) {
          // “Em aberto” por recebimentos: somente faturados e ainda não quitados
          if (isRecebimentoEmAtraso(r)) {
            recDistAtrasoCount += 1;
            recDistAtrasoLiq += vlrLiq;
          } else {
            recDistAbertoNoPrazoCount += 1;
            recDistAbertoNoPrazoLiq += vlrLiq;
          }
        }
      }

      // ===========================
      // INADIMPLÊNCIA (mês): itens NÃO recebidos com forecast no mês
      // ===========================
      if (
        dtQuitPrev &&
        inadimplenciaCutoff &&
        dtQuitPrev >= inadimplenciaCutoff &&
        inRange(dtQuitPrev, mesStart, mesEndExclusive) &&
        !isRecebido(r) &&
        isFaturado(r)
      ) {
        if (!due) {
          // sem data contratual/operacional de vencimento
        } else if (isRecebimentoEmAtraso(r)) {
          inadimplenciaCount += 1;
          inadimplenciaLiq += vlrLiq;
        } else {
          emAbertoNoMesCount += 1;
          emAbertoNoMesLiq += vlrLiq;
        }
      }
    }

    const fatGapPrevMenosReal = fatPrevMesBru - fatRealMesBru;
    const recGapPrevMenosReal = recLiqPrevMes - recLiqRealMes;

    const gaps = {
      fatPrevMenosReal: fatGapPrevMenosReal,
      recPrevMenosReal: recGapPrevMenosReal,
    };

    const pct = {
      fatRealSobrePrev: fatPrevMesBru > 0 ? fatRealMesBru / fatPrevMesBru : null,
      recRealSobrePrev: recLiqPrevMes > 0 ? recLiqRealMes / recLiqPrevMes : null,
      inadimplenciaSobrePrevLiq: recLiqPrevMes > 0 ? inadimplenciaLiq / recLiqPrevMes : null,
    };

    return {
      today,
      mesStart,
      mesEndExclusive,
      // Faturamento (mês)
      fatPrevMesBru,
      fatRealMesBru,
      fatPrevCount,
      fatRealCount,

      fatDistFaturadoBru,
      fatDistLiberadoBru,
      fatDistBloqueadoBru,
      fatDistFaturadoCount,
      fatDistLiberadoCount,
      fatDistBloqueadoCount,

      // Receita líquida (mês, por rec_dtQuitAtual)
      recLiqPrevMes,
      recLiqPrevCount,
      recLiqRealMes,
      recLiqRealCount,

      recDistRecebidoLiq,
      recDistAbertoNoPrazoLiq,
      recDistAtrasoLiq,
      recDistRecebidoCount,
      recDistAbertoNoPrazoCount,
      recDistAtrasoCount,

      // Inadimplência (mês, por due date)
      inadimplenciaLiq,
      inadimplenciaCount,
      emAbertoNoMesLiq,
      emAbertoNoMesCount,

      semDtNfPrev,
      semDtQuit,
      semDtDue,
      gaps,
      pct,
    };
  }, [recs, mesRef]);
}

